/** YYYY-MM-DD in local calendar from Date */
function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export type ProgramAnalysisDateFilterMode = "week" | "month" | "year";

export type DateParamsLike = {
  week?: string;
  year?: string;
  startDate?: string;
  endDate?: string;
};

/**
 * Date range for Explorer livestream grouped-by-channel (requires startDate + endDate).
 * Aligns week mode with Program Analysis week chips (Monday YYYY-MM-DD per week).
 */
export function getLivestreamRankingDateRange(
  dateFilterMode: ProgramAnalysisDateFilterMode,
  selectedWeeks: string[],
  dateParams: DateParamsLike,
): { startDate: string; endDate: string } | null {
  const hasAll = selectedWeeks.includes("all");
  if (hasAll || selectedWeeks.length === 0) {
    return null;
  }

  if (dateFilterMode === "month" || dateFilterMode === "year") {
    if (dateParams.startDate && dateParams.endDate) {
      return { startDate: dateParams.startDate, endDate: dateParams.endDate };
    }
    return null;
  }

  // week mode: dateParams.week may be "YYYY-MM-DD" or comma-separated weeks
  const weekParam = dateParams.week?.trim();
  if (!weekParam) return null;

  const parts = weekParam.split(",").map((p) => p.trim()).filter(Boolean);
  let minStart: Date | null = null;
  let maxEnd: Date | null = null;

  for (const w of parts) {
    const start = new Date(`${w}T12:00:00`);
    if (isNaN(start.getTime())) continue;
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    if (!minStart || start < minStart) minStart = start;
    if (!maxEnd || end > maxEnd) maxEnd = end;
  }

  if (!minStart || !maxEnd) return null;
  return { startDate: toYmd(minStart), endDate: toYmd(maxEnd) };
}

/**
 * Used on ranking-asset routes that only have URL search params (week and/or startDate/endDate).
 */
export function getLivestreamRankingDateRangeFromSearchParams(
  week: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
): { startDate: string; endDate: string } | null {
  if (startDate && endDate) {
    return { startDate, endDate };
  }
  if (week?.trim()) {
    const parts = week.split(",").map((p) => p.trim()).filter(Boolean);
    return getLivestreamRankingDateRange("week", parts, { week });
  }
  return null;
}
